import { FlashbarProps, SelectProps } from '@cloudscape-design/components';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import React, { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

import {
  FoodMenue,
  GetFoodMenuesDocument,
  GetFoodMenuesQuery,
  useCreateFoodMenueMutation,
} from '../../../../generated/graphql';
import { ErrorText } from '../../../../lib/ErrorTexts';
import Form from './Form';
import { createDDBItem } from './lib/Helper';

export const schema = yup
  .object()
  .shape({
    title: yup.string().required(ErrorText.Required),
    price: yup
      .string()
      .matches(/^\d{1,},\d{2}$/, ErrorText.Currency)
      .required(ErrorText.Required),
    category: yup.object().required(ErrorText.Required),
    foodtype: yup.object().required(ErrorText.Required),
  })
  .required();

type CreateProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  notifyDeletedOrUpdated: (resource: FlashbarProps.MessageDefinition) => void;
  notifyInProgress: (resource?: FlashbarProps.MessageDefinition) => void;
  notifyFailed: (failedResource: FlashbarProps.MessageDefinition) => void;
  additiveOptions: SelectProps.Option[];
  allergenOptions: SelectProps.Option[];
  categoryOptions: SelectProps.Option[];
};

export type FormValues = {
  title: string;
  subtitle?: string;
  price: string;
  category: SelectProps.Option;
  foodtype: SelectProps.Option;
  is_active?: boolean;
  productinformation?: SelectProps.Option[];
};

export default function Create({
  visible,
  setVisible,
  notifyInProgress,
  notifyDeletedOrUpdated,
  notifyFailed,
  additiveOptions,
  allergenOptions,
  categoryOptions,
}: CreateProps) {
  const [createFoodMenue, { loading: createLoading }] =
    useCreateFoodMenueMutation();
  const methods = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: {},
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: any) => {
    const entry = createDDBItem(data);
    const messageId = uuidv4();
    notifyInProgress({ id: messageId, header: 'Eintrag wird erstellt...' });
    createFoodMenue({
      variables: { input: entry },
      update: (cache, { data }) => {
        const addData = data?.createFoodMenue;
        const existingData = cache.readQuery({
          query: GetFoodMenuesDocument,
        }) as GetFoodMenuesQuery;
        const oldData = existingData.listFoodMenues?.items as FoodMenue[];

        cache.writeQuery({
          query: GetFoodMenuesDocument,
          data: {
            listFoodMenues: { items: [...oldData, addData] },
          },
        });
      },
    })
      .then((data) => {
        const result = data.data?.createFoodMenue;
        if (result) {
          notifyInProgress();
          notifyDeletedOrUpdated({
            id: messageId,
            type: 'success',
            content: `Eintrag mit dem Namen "${result.title}" wurde erstellt.`,
            header: 'Gespeichert',
            dismissible: true,
          });
          handleCancel();
        }
      })
      .catch((error) => {
        notifyInProgress();
        notifyFailed({
          id: messageId,
          content: JSON.stringify(error),
          header: 'Ein Fehler ist aufgetreten',
          dismissible: true,
        });
      });
  };

  const handleCancel = useCallback(() => {
    setVisible(false);
    methods.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.reset, setVisible]);

  return (
    <FormProvider {...methods}>
      <Modal
        onDismiss={handleCancel}
        visible={visible}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCancel}>
                Abbrechen
              </Button>
              <Button
                variant="primary"
                disabled={
                  isEmpty(methods.formState.dirtyFields) || createLoading
                }
                formAction="submit"
                onClick={() => methods.handleSubmit(onSubmit)()}
              >
                Speichern
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={'Eintrag erstellen'}
      >
        <Form
          additiveOptions={additiveOptions}
          allergenOptions={allergenOptions}
          categoryOptions={categoryOptions}
        />
      </Modal>
    </FormProvider>
  );
}
